// Layer 2 (Engine): graph-adjacent case threads (construct.R9).
// See docs/engines/diagnosis.md § Threads.

import type { IssueThread, IssueSeverity } from '@/versions/v1/schema/advisory-issue';
import type { Case } from '@/versions/v1/schema/case';
import type { Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import { ALARMS, ISSUE_THREADS, VARIATION_EVENTS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { STREAMS, SUBPROCESSES } from '@/versions/v1/shared/bunker-hill-seed';
import { MONITORED_VARIABLES, TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';

const OPEN_STATES: readonly Case['state'][] = [
  'detected',
  'matched',
  'routed',
  'evidence-requested',
  'escalated',
  'diagnosed',
  'outcome-documented',
  'fmea-review',
];

const SEVERITY_RANK: Record<IssueSeverity, number> = {
  high: 0,
  med: 1,
  low: 2,
  monitor: 3,
};

/** Shared resolver — computed cases and governance thread lookup use the same id. */
export function caseIdForVariable(variableId: string): string {
  return `case-${variableId}`;
}

function subprocessLabel(id: string): string {
  return SUBPROCESSES.find((row) => row.id === id)?.name ?? id.replace('sp-', '');
}

function buildUpstream(): Map<string, Set<string>> {
  const adj = new Map<string, Set<string>>();
  for (const stream of STREAMS) {
    if (stream.from === 'external') continue;
    const set = adj.get(stream.to) ?? new Set();
    set.add(stream.from);
    adj.set(stream.to, set);
  }
  return adj;
}

const UPSTREAM = buildUpstream();

/** Plant subprocess order derived from STREAMS (upstream → downstream). */
export function subprocessFlowOrder(): string[] {
  const order: string[] = [];
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const visit = (node: string) => {
    if (visited.has(node)) return;
    if (visiting.has(node)) return;
    visiting.add(node);
    for (const up of UPSTREAM.get(node) ?? []) {
      visit(up);
    }
    visiting.delete(node);
    visited.add(node);
    order.push(node);
  };
  for (const row of SUBPROCESSES) {
    visit(row.id);
  }
  return order;
}

const FLOW_ORDER = subprocessFlowOrder();
const FLOW_INDEX = new Map(FLOW_ORDER.map((id, index) => [id, index]));

export function upstreamNodes(nodeId: string): Set<string> {
  const visited = new Set<string>();
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current)) continue;
    visited.add(current);
    for (const neighbor of UPSTREAM.get(current) ?? []) {
      if (!visited.has(neighbor)) queue.push(neighbor);
    }
  }
  return visited;
}

function nearestCommonUpstream(nodes: string[]): string | undefined {
  if (nodes.length === 0) return undefined;
  let common = upstreamNodes(nodes[0]!);
  for (let i = 1; i < nodes.length; i++) {
    const up = upstreamNodes(nodes[i]!);
    common = new Set([...common].filter((id) => up.has(id)));
  }
  if (common.size === 0) return nodes[0];
  return [...common].sort((a, b) => (FLOW_INDEX.get(b) ?? 0) - (FLOW_INDEX.get(a) ?? 0))[0];
}

function worstSeverity(cases: Case[]): IssueSeverity {
  return cases.reduce<IssueSeverity>((best, row) => {
    return SEVERITY_RANK[row.severity] < SEVERITY_RANK[best] ? row.severity : best;
  }, 'monitor');
}

function caseSetKey(ids: readonly string[]): string {
  return [...ids].sort().join('|');
}

/** Alarm-spawned cases map to the excursion variable the alarm names (seed-honest). */
const ALARM_VARIABLE_IDS: Record<string, string> = {
  'alm-zn-ph': 'mv-zn-ph',
  'alm-ag-conc': 'mv-ag-grade',
  'alm-bm-brg': 'mv-bm-brg-temp',
};

/** Resolve a case's monitored variable — pairs with `caseIdForVariable` for governance lookup. */
export function variableIdForCase(caseItem: Case): string | undefined {
  // #758: an explicit seed link is authoritative — it names the variable on a
  // case the corpus positions there without a variation event or alarm.
  if (caseItem.variableId) return caseItem.variableId;
  if (caseItem.id.startsWith('case-mv-')) {
    return caseItem.id.slice('case-'.length);
  }
  if (caseItem.alarmId && ALARM_VARIABLE_IDS[caseItem.alarmId]) {
    return ALARM_VARIABLE_IDS[caseItem.alarmId];
  }
  if (caseItem.variationEventId) {
    const seeded = VARIATION_EVENTS.find((row) => row.id === caseItem.variationEventId);
    if (seeded?.variableId) return seeded.variableId;
    if (seeded) {
      const map = TAG_MAPPING.find((row) => row.subprocessId === seeded.subprocessId);
      const variable = MONITORED_VARIABLES.find((row) => row.seriesId === map?.seriesId);
      if (variable) return variable.id;
    }
  }
  return undefined;
}

export function threadIdForVariable(
  variableId: string,
  threads: readonly IssueThread[],
  cases: readonly Case[],
): string | undefined {
  const caseById = new Map(cases.map((row) => [row.id, row]));
  const primaryCaseId = caseIdForVariable(variableId);
  for (const thread of threads) {
    if (thread.caseIds.includes(primaryCaseId)) return thread.id;
    for (const caseId of thread.caseIds) {
      const caseItem = caseById.get(caseId);
      if (caseItem && variableIdForCase(caseItem) === variableId) return thread.id;
    }
  }
  return undefined;
}

function subprocessForVariableId(variableId: string): string | undefined {
  const suffix = variableId.replace(/^mv-/, '');
  return TAG_MAPPING.find((row) => row.seriesId === `rs-${suffix}`)?.subprocessId;
}

function subprocessForCase(caseItem: Case): string | undefined {
  if (caseItem.id.startsWith('case-mv-')) {
    return subprocessForVariableId(caseItem.id.slice('case-'.length));
  }
  if (caseItem.variationEventId) {
    const seeded = VARIATION_EVENTS.find((row) => row.id === caseItem.variationEventId);
    if (seeded?.subprocessId) return seeded.subprocessId;
    if (caseItem.variationEventId.startsWith('ex-mv-')) {
      const suffix = caseItem.variationEventId.replace(/^ex-mv-/, '').split('-')[0];
      const map = TAG_MAPPING.find((row) => row.seriesId === `rs-${suffix}`);
      if (map?.subprocessId) return map.subprocessId;
    }
  }
  if (caseItem.alarmId) {
    const alarm = ALARMS.find((row) => row.id === caseItem.alarmId);
    if (alarm?.tag) {
      const byLoop = TAG_MAPPING.find(
        (row) => row.loopTag === alarm.tag || row.instrumentTag === alarm.tag,
      );
      if (byLoop?.subprocessId) return byLoop.subprocessId;
    }
  }
  return undefined;
}

function memberSetHash(ids: readonly string[]): string {
  const sorted = [...ids].sort().join('|');
  let hash = 2166136261;
  for (let i = 0; i < sorted.length; i++) {
    hash ^= sorted.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

class UnionFind {
  private parent = new Map<string, string>();

  find(id: string): string {
    const existing = this.parent.get(id);
    if (!existing) {
      this.parent.set(id, id);
      return id;
    }
    if (existing !== id) {
      const root = this.find(existing);
      this.parent.set(id, root);
      return root;
    }
    return id;
  }

  union(a: string, b: string): void {
    const rootA = this.find(a);
    const rootB = this.find(b);
    if (rootA !== rootB) this.parent.set(rootB, rootA);
  }
}

function connectedNodeComponents(nodes: Set<string>): Map<string, string[]> {
  const uf = new UnionFind();
  for (const node of nodes) uf.find(node);
  for (const stream of STREAMS) {
    if (stream.from === 'external') continue;
    if (nodes.has(stream.from) && nodes.has(stream.to)) {
      uf.union(stream.from, stream.to);
    }
  }
  const groups = new Map<string, string[]>();
  for (const node of nodes) {
    const root = uf.find(node);
    const list = groups.get(root) ?? [];
    list.push(node);
    groups.set(root, list);
  }
  return groups;
}

export function mergeCasesForThreading(
  seeded: readonly Case[],
  diagnosis: Diagnosis,
): Case[] {
  const byId = new Map(seeded.map((row) => [row.id, row]));
  for (const computed of diagnosis.cases) {
    if (!byId.has(computed.id)) byId.set(computed.id, computed);
  }
  return [...byId.values()];
}

export function threadsFor(
  cases: readonly Case[],
  diagnosis: Diagnosis,
): IssueThread[] {
  const allCases = mergeCasesForThreading(cases, diagnosis);
  const open = allCases.filter((row) => OPEN_STATES.includes(row.state));
  const seededKeys = new Set(ISSUE_THREADS.map((row) => caseSetKey(row.caseIds)));
  const seededCaseIds = new Set(ISSUE_THREADS.flatMap((row) => row.caseIds));

  const unthreaded = open.filter((row) => !seededCaseIds.has(row.id));
  const byNode = new Map<string, Case[]>();
  for (const caseItem of unthreaded) {
    const node = subprocessForCase(caseItem);
    if (!node) continue;
    const list = byNode.get(node) ?? [];
    list.push(caseItem);
    byNode.set(node, list);
  }

  const nodesWithCases = new Set(byNode.keys());
  const nodeComponents = connectedNodeComponents(nodesWithCases);
  const computed: IssueThread[] = [];
  const usedCaseIds = new Set<string>();

  for (const componentNodes of nodeComponents.values()) {
    const groupCases = componentNodes.flatMap((node) => byNode.get(node) ?? []);
    const ids = groupCases.map((row) => row.id);
    const uniqueIds = [...new Set(ids)].filter((id) => !usedCaseIds.has(id));
    if (uniqueIds.length < 2) continue;
    const key = caseSetKey(uniqueIds);
    if (seededKeys.has(key)) continue;

    const memberById = new Map(groupCases.map((row) => [row.id, row]));
    const members = uniqueIds
      .map((id) => memberById.get(id))
      .filter((row): row is Case => Boolean(row));
    const sortedIds = [...uniqueIds].sort((left, right) => {
      const leftCase = memberById.get(left);
      const rightCase = memberById.get(right);
      return (
        (FLOW_INDEX.get(subprocessForCase(leftCase!) ?? '') ?? 0) -
        (FLOW_INDEX.get(subprocessForCase(rightCase!) ?? '') ?? 0)
      );
    });
    const signalCount = sortedIds.length;
    const upstream = nearestCommonUpstream(componentNodes) ?? componentNodes[0]!;
    computed.push({
      id: `thread-computed-${upstream}-${memberSetHash(sortedIds)}`,
      rootNodeId: upstream,
      title: `${subprocessLabel(upstream)} → ${signalCount} downstream signals`,
      caseIds: sortedIds,
      signalCount,
      worstSeverity: worstSeverity(members),
      computed: true,
    });
    for (const id of sortedIds) usedCaseIds.add(id);
  }

  return [...ISSUE_THREADS, ...computed];
}

function threadDisplayLabel(thread: IssueThread): string {
  if (thread.rootNodeId) return subprocessLabel(thread.rootNodeId);
  const slash = thread.title.indexOf(' / ');
  if (slash > 0) return thread.title.slice(0, slash);
  const arrow = thread.title.indexOf(' → ');
  if (arrow > 0) return thread.title.slice(0, arrow);
  return thread.title;
}

export function threadSummaries(
  cases: readonly Case[],
  diagnosis: Diagnosis,
): { id: string; label: string; rootNodeId: string; caseIds: string[]; openCount: number }[] {
  const allThreads = threadsFor(cases, diagnosis);
  const caseById = new Map(mergeCasesForThreading(cases, diagnosis).map((row) => [row.id, row]));
  return allThreads.map((thread) => {
    const openCount = thread.caseIds.filter((id) => {
      const row = caseById.get(id);
      return row && OPEN_STATES.includes(row.state);
    }).length;
    return {
      id: thread.id,
      label: threadDisplayLabel(thread),
      rootNodeId: thread.rootNodeId ?? '',
      caseIds: [...thread.caseIds],
      openCount,
    };
  });
}

export function threadForCase(
  caseId: string,
  cases: readonly Case[],
  diagnosis: Diagnosis,
): IssueThread | undefined {
  return threadsFor(cases, diagnosis).find((row) => row.caseIds.includes(caseId));
}
